import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { usePublicReport, PublicReportCategoryScores } from "../../../hooks/usePublicReport";

const CATEGORY_META: { key: keyof PublicReportCategoryScores; label: string; color: string; bg: string }[] = [
  { key: "marketOpportunity",    label: "Market Opportunity",      color: "#3B82F6", bg: "#EFF6FF" },
  { key: "customerUnderstanding",label: "Customer Understanding",  color: "#8B5CF6", bg: "#F5F3FF" },
  { key: "valueAndProduct",      label: "Value & Product",          color: "#EC4899", bg: "#FDF2F8" },
  { key: "businessModel",        label: "Business Model",           color: "#F59E0B", bg: "#FFFBEB" },
  { key: "validationExecution",  label: "Validation & Execution",   color: "#10B981", bg: "#ECFDF5" },
];

function scoreColor(score: number): string {
  if (score >= 70) return "#10B981";
  if (score >= 40) return "#F59E0B";
  return "#EF4444";
}

function scoreLabel(score: number): string {
  if (score >= 70) return "Strong";
  if (score >= 40) return "Developing";
  return "Needs Work";
}

export const PublicReportPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { fetchReport, report, isFetching, error } = usePublicReport();

  useEffect(() => {
    if (slug) {
      fetchReport(slug);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (isFetching) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loadingWrap}>
            <div style={styles.spinner} />
            <p style={{ color: "#475569", fontSize: "15px", margin: 0 }}>Loading report…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={styles.notFoundIcon}>!</div>
            <h1 style={{ fontSize: "22px", fontWeight: 600, color: "#0F172A", marginBottom: "8px" }}>
              Report Not Found
            </h1>
            <p style={{ fontSize: "15px", color: "#475569", marginBottom: "32px" }}>
              {error || "This report doesn't exist or the link may have expired."}
            </p>
            <Link to="/auth/registration" style={styles.ctaBtn}>
              Validate Your Own Idea
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const overall = report.overallScore;
  const overallColor = scoreColor(overall);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.brandBadge}>Business Foundry</div>
          <h1 style={styles.reportTitle}>{report.projectTitle}</h1>
          <p style={{ fontSize: "14px", color: "#6D7A8A", margin: "6px 0 0" }}>
            Validation Report
          </p>
        </div>

        {/* Overall Score Hero */}
        <div style={styles.scoreHero}>
          <div style={styles.scoreRing}>
            <svg viewBox="0 0 120 120" width="120" height="120">
              <circle
                cx="60" cy="60" r="52"
                fill="none" stroke="#F1F5F9" strokeWidth="10"
              />
              <circle
                cx="60" cy="60" r="52"
                fill="none"
                stroke={overallColor}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${(overall / 100) * 326.7} 326.7`}
                transform="rotate(-90 60 60)"
                style={{ transition: "stroke-dasharray 0.6s ease-out" }}
              />
            </svg>
            <div style={styles.scoreInner}>
              <span style={{ fontSize: "28px", fontWeight: 700, color: overallColor, fontFamily: "monospace" }}>
                {overall}%
              </span>
            </div>
          </div>
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: overall >= 70 ? "#D1FAE5" : overall >= 40 ? "#FEF3C7" : "#FEE2E2",
                color: overallColor,
                borderRadius: "9999px",
                padding: "4px 14px",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "10px",
              }}
            >
              <span style={{ fontSize: "10px" }}>●</span>
              {scoreLabel(overall)} Validation
            </div>
            <h2 style={{ fontSize: "22px", fontWeight: 600, color: "#0F172A", marginBottom: "6px" }}>
              Overall Score
            </h2>
            <p style={{ fontSize: "14px", color: "#475569", margin: 0, maxWidth: "380px" }}>
              Scored across 5 categories: Market Opportunity, Customer Understanding, Value &amp; Product, Business Model, and Validation &amp; Execution.
            </p>
          </div>
        </div>

        {/* Category Scores */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Category Breakdown</h3>
          <div style={styles.categoryGrid}>
            {CATEGORY_META.map(({ key, label, color, bg }) => {
              const score = report.categoryScores[key] ?? 0;
              const reason = report.categoryReasons?.[key as keyof typeof report.categoryReasons];
              return (
                <div key={key} style={{ ...styles.categoryCard, background: bg, borderColor: color + "33" }}>
                  <div style={{ ...styles.categoryBar, background: color + "22" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${score}%`,
                        background: color,
                        borderRadius: "2px",
                        transition: "width 0.6s ease-out",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>{label}</span>
                    <span style={{ fontSize: "18px", fontWeight: 700, color, fontFamily: "monospace", flexShrink: 0 }}>
                      {score}%
                    </span>
                  </div>
                  {reason && (
                    <p style={{ fontSize: "12px", color: "#475569", margin: 0, lineHeight: 1.5 }}>{reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Strengths */}
        {report.strengths.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <span style={{ color: "#10B981", marginRight: "8px" }}>↑</span>
              Strengths
            </h3>
            <ul style={styles.list}>
              {report.strengths.map((s, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={{ ...styles.listDot, background: "#D1FAE5", color: "#10B981" }}>✓</span>
                  <span style={{ fontSize: "14px", color: "#0F172A" }}>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Red Flags */}
        {report.redFlags.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <span style={{ color: "#EF4444", marginRight: "8px" }}>⚑</span>
              Red Flags
            </h3>
            <ul style={styles.list}>
              {report.redFlags.map((f, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={{ ...styles.listDot, background: "#FEE2E2", color: "#EF4444" }}>!</span>
                  <span style={{ fontSize: "14px", color: "#0F172A" }}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendations */}
        {report.recommendations.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              <span style={{ color: "#3B82F6", marginRight: "8px" }}>→</span>
              Recommendations
            </h3>
            <ul style={styles.list}>
              {report.recommendations.map((r, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={{ ...styles.listDot, background: "#DBEAFE", color: "#3B82F6" }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize: "14px", color: "#0F172A" }}>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA Footer */}
        <div style={styles.ctaSection}>
          <div style={styles.ctaLogo}>Business Foundry</div>
          <h3 style={{ fontSize: "22px", fontWeight: 700, color: "#0F172A", marginBottom: "10px" }}>
            Validate your own business idea
          </h3>
          <p style={{ fontSize: "14px", color: "#475569", marginBottom: "28px", maxWidth: "440px", margin: "0 auto 28px" }}>
            Business Foundry guides you through 24 structured validation steps, scores your idea with AI, and helps you build and launch your MVP.
          </p>
          <a
            href="https://app.businessfactory.ai/auth/registration"
            style={styles.ctaBtn}
          >
            Start Validating for Free →
          </a>
          <p style={{ fontSize: "12px", color: "#94A3B8", marginTop: "14px" }}>
            Free account · No credit card required
          </p>
        </div>

        {/* Footer meta */}
        <div style={{ textAlign: "center", padding: "20px", borderTop: "1px solid #E2E8F0" }}>
          <p style={{ fontSize: "12px", color: "#94A3B8", margin: 0 }}>
            Generated on {new Date(report.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} via Business Foundry
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "100vh",
    background: "#F8FAFC",
    display: "flex",
    justifyContent: "center",
    padding: "40px 16px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif",
  },
  container: {
    width: "100%",
    maxWidth: "720px",
    background: "#FFFFFF",
    borderRadius: "16px",
    border: "1px solid #E2E8F0",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.03)",
    overflow: "hidden",
    alignSelf: "flex-start",
  },
  header: {
    padding: "40px 40px 32px",
    borderBottom: "1px solid #E2E8F0",
    background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
  },
  brandBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#3B82F6",
    color: "white",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderRadius: "6px",
    padding: "4px 10px",
    marginBottom: "16px",
  },
  reportTitle: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#0F172A",
    letterSpacing: "-0.01em",
    margin: 0,
    lineHeight: 1.3,
  },
  scoreHero: {
    padding: "32px 40px",
    display: "flex",
    alignItems: "center",
    gap: "32px",
    borderBottom: "1px solid #E2E8F0",
    flexWrap: "wrap",
  },
  scoreRing: {
    position: "relative",
    flexShrink: 0,
    width: "120px",
    height: "120px",
  },
  scoreInner: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  section: {
    padding: "28px 40px",
    borderBottom: "1px solid #F1F5F9",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0F172A",
    marginBottom: "16px",
  },
  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "12px",
  },
  categoryCard: {
    borderRadius: "10px",
    border: "1px solid",
    padding: "16px",
  },
  categoryBar: {
    height: "4px",
    borderRadius: "2px",
    marginBottom: "12px",
    overflow: "hidden",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  listDot: {
    width: "22px",
    height: "22px",
    borderRadius: "9999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: 700,
    flexShrink: 0,
    marginTop: "1px",
  },
  ctaSection: {
    padding: "48px 40px",
    textAlign: "center",
    background: "linear-gradient(135deg, #F5F3FF 0%, #EFF6FF 100%)",
    borderBottom: "1px solid #E2E8F0",
  },
  ctaLogo: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#8B5CF6",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "16px",
  },
  ctaBtn: {
    display: "inline-block",
    background: "#3B82F6",
    color: "white",
    padding: "14px 28px",
    borderRadius: "10px",
    fontWeight: 600,
    fontSize: "15px",
    textDecoration: "none",
    transition: "all 150ms ease-out",
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    gap: "16px",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #E2E8F0",
    borderTopColor: "#3B82F6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  notFoundIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: "#FEE2E2",
    color: "#EF4444",
    fontSize: "28px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
};

export default PublicReportPage;
